import { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { UserProfileContext } from '../../../../context/userProfileContext';
import { InsurancePolicy, InsuredPersonCoverageSelection, SelectedCoverage } from '../../../../interfaces/InsurancePolicy';
import { InsuranceAppendix } from '../../../../interfaces/InsuranceAppendix';
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import PolicyInputFields from './PolicyInputFields';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import { initialInsurancePolicyState } from './initialPolicyAndAppendixState';
import { addAdditionalInfoToPolicy } from './appendixAndPolicyCreationFunctions';
import { useProductsContext } from '../insuranceProductsContext';
import globalErrorHandler from '../../../../utils/globalErrorHandler';
import { useTranslation } from 'react-i18next';
import { Company } from '../../../../interfaces/Company';
import { getCompanies } from '../../../../utils/api/companies';
import PremiumChangeTableModal from './PremiumChangeTableModal';
import CompanyComparisonModal from './CompanyComparisonModal';

// Helper function to convert existing appendices to insuredPersonCoverages format
const convertAppendixesToPersonCoverages = (appendices: InsuranceAppendix[] | undefined): InsuredPersonCoverageSelection[] => {
    if (!appendices || appendices.length === 0) return [];

    // Group appendices by insured person
    const personCoveragesMap = new Map<string, InsuredPersonCoverageSelection>();

    for (const appendix of appendices) {
        if (!appendix.insuredPerson?.idNumber) continue;

        const idNumber = appendix.insuredPerson.idNumber;
        const existing = personCoveragesMap.get(idNumber);

        // Get coverage type from domain or insurancePricing
        const coverageType = appendix.domain || (appendix.insurancePricing as any)?.coverageType;
        if (!coverageType) continue;

        // Get the insurance pricing ID
        const insurancePricingId =
            typeof appendix.insurancePricing === 'string' ? appendix.insurancePricing : (appendix.insurancePricing as any)?._id || '';

        // Create proper SelectedCoverage object
        const coverage: SelectedCoverage = {
            coverageType,
            insurancePricingId,
        };

        if (existing) {
            // Check if this coverage type already exists
            const alreadyExists = existing.selectedCoverages.some((sc) => typeof sc === 'object' && sc.coverageType === coverageType);
            if (!alreadyExists) {
                existing.selectedCoverages.push(coverage);
            }
        } else {
            personCoveragesMap.set(idNumber, {
                insuredPerson: {
                    name: appendix.insuredPerson.name,
                    idNumber: appendix.insuredPerson.idNumber,
                },
                selectedCoverages: [coverage],
            });
        }
    }

    return Array.from(personCoveragesMap.values());
};

// Helper to create a fingerprint of premium-affecting fields (for edit mode)
const getPremiumFieldsFingerprint = (policy: InsurancePolicy): string => {
    const getCompanyId = (company: any) => (typeof company === 'string' ? company : company?._id);

    const parts = [
        getCompanyId(policy.company) || '',
        policy.branch || '',
        String(policy.totalCoverage || 0),
        policy.insuranceDiscount || '',
        (policy.insuredList || [])
            .map((p) => `${p.idNumber}:${p.occupation || ''}:${p.occupationMultiplier ?? 1}`)
            .sort()
            .join(','),
        (policy.insuredPersonCoverages || [])
            .map(
                (c) =>
                    `${c.insuredPerson.idNumber}:${c.selectedCoverages
                        .map((sc) => (typeof sc === 'string' ? sc : sc.insurancePricingId))
                        .sort()
                        .join('|')}`,
            )
            .sort()
            .join(';'),
        // Include mortgage details for mortgage insurance
        (policy.mortgageDetails || []).map((d) => `${d.amount || 0}:${d.period || 0}:${d.interestRate || 0}`).join(','),
    ];

    return parts.join('##');
};

type InsurancePolicyModalMode = 'create' | 'edit';

interface InsurancePolicyModalProps {
    show: boolean;
    onHide: () => void;
    mode: InsurancePolicyModalMode;
    // For create mode
    replacementPolicies?: InsurancePolicy[];
    productCreationHandler?: (newInsurancePolicy: InsurancePolicy) => void;
    // For edit mode
    currentPolicyState?: InsurancePolicy;
    policyUpdateHandler?: (updatedPolicy: InsurancePolicy) => void;
}

const InsurancePolicyModal = ({
    show,
    onHide,
    mode,
    replacementPolicies = [],
    productCreationHandler,
    currentPolicyState,
    policyUpdateHandler,
}: InsurancePolicyModalProps) => {
    const { t } = useTranslation('insurance');
    const { profileContextData } = useContext(UserProfileContext);
    const { consts } = useProductsContext();
    const { client } = profileContextData;

    const isCreateMode = mode === 'create';
    const isEditMode = mode === 'edit';

    // Initialize policy state based on mode
    const getInitialPolicy = (): InsurancePolicy => {
        if (isEditMode && currentPolicyState) {
            return currentPolicyState;
        }
        return initialInsurancePolicyState;
    };

    const [policy, setPolicy] = useState<InsurancePolicy>(getInitialPolicy);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showPremiumTable, setShowPremiumTable] = useState(false);
    const [showCompanyComparison, setShowCompanyComparison] = useState(false);
    const [comparisonPolicy, setComparisonPolicy] = useState<InsurancePolicy | null>(null);
    const [selectedPolicyForPremiumTable, setSelectedPolicyForPremiumTable] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [modalKey, setModalKey] = useState(0);

    // For edit mode: track fingerprint to detect changes
    const initialFingerprintRef = useRef<string | null>(null);
    const updateCountRef = useRef(0);

    // Filter and sort companies
    const allowedShortNames = ['הפניקס', 'מגדל', 'הראל', 'כלל', 'מנורה'];

    const filteredCompanies = useMemo(() => {
        const source = isCreateMode ? companies : consts.companies || [];
        const filtered = source.filter((c: Company) => c.shortName && allowedShortNames.includes(c.shortName));
        filtered.sort((a: Company, b: Company) => allowedShortNames.indexOf(a.shortName!) - allowedShortNames.indexOf(b.shortName!));
        return filtered;
    }, [isCreateMode, companies, consts.companies]);

    // Handle selection from company comparison modal
    const handleSelectCompanyDiscount = (companyId: string, discountId: string | undefined) => {
        setPolicy({
            ...policy,
            company: companyId,
            insuranceDiscount: discountId,
            insuredPersonCoverages: [], // Reset coverages when company changes
        });
    };

    // Fetch companies for create mode
    useEffect(() => {
        if (!isCreateMode) return;

        const fetchCompanies = async () => {
            try {
                const response = await getCompanies({ type: 'insurance' });
                const allCompanies = response.companies || [];
                const filtered = allCompanies.filter((c: Company) => c.shortName && allowedShortNames.includes(c.shortName));
                filtered.sort((a: Company, b: Company) => allowedShortNames.indexOf(a.shortName!) - allowedShortNames.indexOf(b.shortName!));
                setCompanies(filtered);

                if (!policy.company && filtered.length > 0) {
                    setPolicy((prevState) => ({
                        ...prevState,
                        company: filtered[0],
                        replacementPolicies,
                    }));
                }
            } catch (err) {
                globalErrorHandler(err);
            }
        };

        fetchCompanies();
    }, [isCreateMode]);

    // Handle replacement policies for create mode
    useEffect(() => {
        if (!isCreateMode || !replacementPolicies?.length) return;

        const insuranceStartDate = new Date();
        insuranceStartDate.setMonth(insuranceStartDate.getMonth() + 1);
        insuranceStartDate.setDate(1);

        // Convert existing appendices with insurancePricing to insuredPersonCoverages
        const existingPersonCoverages = convertAppendixesToPersonCoverages(replacementPolicies[0].appendices);

        const tmp = {
            ...replacementPolicies[0],
            totalPremium: 0,
            policyNumber: '',
            insuranceStartDate,
            insuranceEndDate: undefined,
            replacementPolicies,
            insuredPersonCoverages: existingPersonCoverages,
        };

        setPolicy(tmp);
    }, [isCreateMode, replacementPolicies]);

    // Reset state when modal opens (for edit mode)
    useEffect(() => {
        if (isEditMode && show && currentPolicyState) {
            // Convert appendices to insuredPersonCoverages if not already present
            const existingPersonCoverages = currentPolicyState.insuredPersonCoverages?.length
                ? currentPolicyState.insuredPersonCoverages
                : convertAppendixesToPersonCoverages(currentPolicyState.appendices);

            setPolicy({
                ...currentPolicyState,
                insuredPersonCoverages: existingPersonCoverages,
            });
            updateCountRef.current = 0;
            initialFingerprintRef.current = null;
            setValidationError(null);
            setModalKey((prev) => prev + 1);
        }
    }, [isEditMode, show, currentPolicyState]);

    // Wrap setPolicy to capture fingerprint after initialization (for edit mode)
    const handleSetPolicy = (newPolicy: InsurancePolicy) => {
        if (isEditMode) {
            updateCountRef.current += 1;
            if (updateCountRef.current <= 3) {
                initialFingerprintRef.current = getPremiumFieldsFingerprint(newPolicy);
            }
        }
        setPolicy(newPolicy);
    };

    // Check if premium-affecting fields have changed (for edit mode)
    const currentFingerprint = getPremiumFieldsFingerprint(policy);
    const hasUnsavedChanges = isEditMode && initialFingerprintRef.current !== null && currentFingerprint !== initialFingerprintRef.current;

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        // Validation for create mode
        if (isCreateMode) {
            const requiredFields = ['company', 'branch', 'insuranceStartDate'];
            const missingFields = requiredFields.filter((field) => !policy[field as keyof InsurancePolicy]);
            if (missingFields.length > 0) {
                setValidationError(`${t('The following fields are required')}: ${missingFields.map((field) => t(field)).join(', ')}`);
                return;
            }
        }

        // Validate mortgage details for mortgage insurance
        if (policy.branch === 'mortgageInsurance') {
            const mortgageDetails = policy.mortgageDetails || [];
            if (mortgageDetails.length === 0) {
                setValidationError(t('Please add at least one mortgage line'));
                return;
            }
            for (let i = 0; i < mortgageDetails.length; i++) {
                const detail = mortgageDetails[i];
                if (!detail.amount || detail.amount <= 0) {
                    setValidationError(`${t('Mortgage line')} ${i + 1}: ${t('Amount must be greater than 0')}`);
                    return;
                }
                if (!detail.period || detail.period <= 0) {
                    setValidationError(`${t('Mortgage line')} ${i + 1}: ${t('Period must be greater than 0')}`);
                    return;
                }
                if (detail.interestRate === undefined || detail.interestRate < 0) {
                    setValidationError(`${t('Mortgage line')} ${i + 1}: ${t('Interest rate must be 0 or greater')}`);
                    return;
                }
            }
        }

        // Validate that at least one person is selected
        const insuredList = policy.insuredList || [];
        if (insuredList.length === 0) {
            setValidationError(t('Please select at least one insured person'));
            return;
        }

        // Validate that each person in insuredList has at least one insurance pricing selected
        const insuredPersonCoverages = policy.insuredPersonCoverages || [];

        for (const person of insuredList) {
            const personCoverage = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === person.idNumber);
            const coverages = personCoverage?.selectedCoverages;
            const validCoverages = Array.isArray(coverages)
                ? coverages.filter((c) => typeof c === 'object' && c !== null && c.coverageType && c.insurancePricingId)
                : [];
            if (!personCoverage || validCoverages.length === 0) {
                setValidationError(`${t('Please select at least one coverage for')} ${person.name}`);
                return;
            }
        }

        setIsSaving(true);

        try {
            if (isCreateMode && productCreationHandler) {
                const finalPolicy = addAdditionalInfoToPolicy(policy, client?._id ?? '', profileContextData.currentRound?._id ?? '');
                setPolicy(finalPolicy);
                await productCreationHandler(finalPolicy);
            } else if (isEditMode && policyUpdateHandler) {
                await policyUpdateHandler(policy);
            }
            setIsSaving(false);
            onHide();
        } catch (error) {
            setIsSaving(false);
            globalErrorHandler(error);
        }
    };

    // Determine policy ID for premium table
    const policyIdForPremiumTable = isCreateMode
        ? selectedPolicyForPremiumTable
        : currentPolicyState?._id;

    // Modal title
    const getModalTitle = () => {
        if (isEditMode) {
            return 'עריכת פוליסה';
        }
        if (!replacementPolicies?.length) {
            return t('New Policy');
        }
        if (replacementPolicies.length === 1) {
            return `${t('Replacement Policy')} (${t('one policy will be replaced')})`;
        }
        return `${t('Replacement Policy')} (${t('instead of')} ${replacementPolicies?.length} ${t('policies')})`;
    };

    // Premium table button
    const renderPremiumTableButton = () => {
        if (isCreateMode) {
            if (!replacementPolicies?.length || !replacementPolicies[0]._id) return null;
            return (
                <Button
                    variant="outline-info"
                    size="sm"
                    onClick={(e) => {
                        e.preventDefault();
                        setSelectedPolicyForPremiumTable(replacementPolicies[0]._id);
                        setShowPremiumTable(true);
                    }}
                    style={{ fontSize: '0.85rem', padding: '0.25rem 0.75rem' }}
                >
                    <i className="mdi mdi-table-eye me-1"></i>
                    התפתחות פרמיה
                </Button>
            );
        }

        // Edit mode
        if (!currentPolicyState?._id) return null;

        const button = (
            <Button
                variant="outline-info"
                size="sm"
                onClick={(e) => {
                    e.preventDefault();
                    setShowPremiumTable(true);
                }}
                style={{ fontSize: '0.85rem', padding: '0.25rem 0.75rem' }}
            >
                <i className="mdi mdi-table-eye me-1"></i>
                {t('Premium Development')}
            </Button>
        );

        if (hasUnsavedChanges) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="premium-dev-tooltip">{t('Save policy to view premium development')}</Tooltip>}
                >
                    <span>{button}</span>
                </OverlayTrigger>
            );
        }
        return button;
    };

    const headerClass = isCreateMode ? 'bg-success' : 'bg-primary';
    const buttonClass = isCreateMode ? 'btn-success' : 'btn-primary';
    const saveButtonText = isSaving ? (isCreateMode ? t('Saving...') : 'שומר...') : (isCreateMode ? t('Save') : 'שמור');

    return (
        <>
            <Modal
                show={show && !showPremiumTable && !showCompanyComparison}
                onHide={onHide}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                dir="rtl"
                size="lg"
                onClick={(e: any) => e.stopPropagation()}
            >
                <div dir="rtl">
                    <Modal.Header
                        className={headerClass}
                        closeButton
                        style={isEditMode ? { flexDirection: 'row-reverse' } : { textAlign: 'right' }}
                    >
                        <Modal.Title className="text-white" style={{ textAlign: 'right', width: '100%' }}>
                            {getModalTitle()}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ textAlign: 'right', direction: 'rtl' }}>
                        {validationError && (
                            <Alert variant="danger" onClose={() => setValidationError(null)} dismissible>
                                {validationError}
                            </Alert>
                        )}
                        <Form onSubmit={handleFormSubmit} dir="rtl">
                            <fieldset>
                                <PolicyInputFields
                                    key={isEditMode ? modalKey : undefined}
                                    newInsurancePolicy={policy}
                                    setNewInsurancePolicy={isEditMode ? handleSetPolicy : setPolicy}
                                    companies={filteredCompanies}
                                    onCompanyComparisonClick={(policyWithCoverage) => {
                                        setComparisonPolicy(policyWithCoverage);
                                        setShowCompanyComparison(true);
                                    }}
                                    additionalActions={renderPremiumTableButton()}
                                />
                            </fieldset>

                            <LaddaButton
                                type="submit"
                                loading={isSaving}
                                data-style={EXPAND_LEFT}
                                className={`right btn ${buttonClass} btn-lg w-${isSaving ? '50' : '25'} mt-4 mb-3`}
                            >
                                {saveButtonText}
                            </LaddaButton>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer></Modal.Footer>
                </div>
            </Modal>

            {/* Premium Change Table Modal */}
            {policyIdForPremiumTable && (
                <PremiumChangeTableModal
                    show={showPremiumTable}
                    onHide={() => {
                        setShowPremiumTable(false);
                        if (isCreateMode) {
                            setSelectedPolicyForPremiumTable(null);
                        }
                    }}
                    policyId={policyIdForPremiumTable}
                />
            )}

            {/* Company Comparison Modal */}
            {comparisonPolicy && (
                <CompanyComparisonModal
                    show={showCompanyComparison}
                    onHide={() => setShowCompanyComparison(false)}
                    companies={filteredCompanies}
                    policy={comparisonPolicy}
                    onSelectCompanyDiscount={handleSelectCompanyDiscount}
                />
            )}
        </>
    );
};

export default InsurancePolicyModal;
